import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { Card } from "@/shared/components/ui/card";
import { cn } from "@/infrastructure/lib/utils";
import { Settings as ProcessIcon } from '@mui/icons-material';
import { useNodes, type Node } from '../hooks/useNodes';
import { Skeleton } from '@/shared/components/ui/skeleton';
import './workflow.css';

interface NodeListProps {
  onNodeDrag?: (event: React.DragEvent, nodeType: string) => void;
}

interface NodeCardProps {
  node: Node;
  onDragStart: (e: React.DragEvent, node: Node) => void;
}

function NodeCard({ node, onDragStart }: NodeCardProps) {
  const [imgError, setImgError] = useState(false);

  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart(e, node)}
      className={cn(
        "cursor-grab p-2.5 hover:bg-accent/50 transition-colors",
        "flex items-center gap-3 group bg-background/40"
      )}
    >
      <div className="relative w-8 h-8 flex-shrink-0 rounded-md bg-background p-1.5 ring-1 ring-border">
        {node.logoUrl && !imgError ? (
          <img
            src={node.logoUrl}
            alt={node.name}
            className="w-full h-full object-contain"
            onError={() => setImgError(true)}
          />
        ) : (
          <ProcessIcon className="h-full w-full text-foreground/70" />
        )}
      </div>
      <div className="space-y-1 flex-1 min-w-0">
        <h4 className="text-sm font-medium leading-none truncate">
          {node.name}
        </h4>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {node.description}
        </p>
      </div>
    </Card>
  );
}

export default function NodeList({ onNodeDrag }: NodeListProps) {
  const { t } = useTranslation();
  const { nodes, loading, error } = useNodes();

  const onDragStart = (event: React.DragEvent, node: Node) => {
    const nodeData = {
      id: node.id,
      name: node.name,
      description: node.description,
      type: node.type,
      category: node.category,
      logoUrl: node.logoUrl,
      settings: node.settings,
      inputSchema: node.inputSchema,
      outputSchema: node.outputSchema
    };

    event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
    onNodeDrag?.(event, node.type);
  };

  if (error) {
    return (
      <div className="p-4 text-sm text-red-500">
        {t('common.errors.loading')}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="workflow-node-list-container">
      <div className="p-4 border-b">
        <h3 className="font-medium text-sm">
          {t('agents.workflow.components')}
        </h3>
      </div>
      <div className="workflow-node-list-scrollable">
        <div>
          <Accordion type="multiple" className="w-full">
            {nodes.map(category => (
              <AccordionItem value={category.category || 'uncategorized'} key={category.category || 'uncategorized'}>
                <AccordionTrigger className="text-sm hover:no-underline px-4">
                  {t(`agents.workflow.categories.${category.category}`)}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-1.5 p-2">
                    {category.nodes.map(node => (
                      <NodeCard key={node.id} node={node} onDragStart={onDragStart} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
